import { useTheme } from "../contexts/ThemeSelection";

/** Parse any CSS color (hex, rgb) into [r,g,b] */
const parseColor = (color: string): [number, number, number] => {
  const hex = color.replace("#", "");
  if (/^[0-9a-f]{3,8}$/i.test(hex)) {
    const full =
      hex.length === 3
        ? hex.split("").map((c) => c + c).join("")
        : hex;
    return [
      Number.parseInt(full.slice(0, 2), 16),
      Number.parseInt(full.slice(2, 4), 16),
      Number.parseInt(full.slice(4, 6), 16),
    ];
  }
  const rgbMatch = /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(color);
  if (rgbMatch) {
    return [+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]];
  }
  return [128, 128, 128];
};

/** Relative luminance (WCAG formula) */
const luminance = (color: string): number => {
  const [r, g, b] = parseColor(color).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Pick readable text color for a given background — biased toward white
 *  so that medium-bright accent colours (greens, teals) get white text. */
export const contrastText = (bg: string): string =>
  luminance(bg) > 0.7 ? "#1a1a2e" : "#ffffff";

/** Is the given color a dark background? (luminance < 0.2) */
export const isDarkBg = (bg: string): boolean => luminance(bg) < 0.2;

/**
 * Shared hook for getting the app's accent color + matching text.
 * Every button/CTA should use this instead of computing its own accent.
 *
 * `accentText` is picked based on the overall theme brightness, not
 * the accent luminance alone — so dark themes always get white button
 * text and light themes always get dark button text.
 */
const useAccentColor = () => {
  const { theme, activeTheme } = useTheme();
  const t = theme[activeTheme];
  const accent = t.secondaryColor || t.todayColor || t.altColor || t.color;
  const darkTheme = isDarkBg(t.backgroundColor);
  const accentText = contrastText(accent);
  const subtitle = t.textUnavailable || t.altColor;

  return { accent, accentText, subtitle, darkTheme };
};

export default useAccentColor;
